import React, { lazy, Suspense, useState, useEffect } from 'react';
import Home from './pages/Home';
import Register, { type RegisterDraft } from './pages/Register';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import SearchResults from './pages/SearchResults';
import ProductDetail from './pages/ProductDetail';
import UserDetails from './pages/UserDetails';
import Profile from './pages/Profile';
import Favorites from './pages/Favorites';
import Settings from './pages/Settings';
import Locations from './pages/Locations';
import MyAnnouncements from './pages/MyAnnouncements';
import CreateProduct from './pages/CreateProduct';
import EditProduct from './pages/EditProduct';
import Messages from './pages/Messages';
import Orders from './pages/Orders';
import OrderStatistics from './pages/OrderStatistics';
import OrderReport from './pages/OrderReport';
import LoadingIndicator from './components/LoadingIndicator';
import { t } from './translations';
import LegalPage from './pages/LegalPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import SearchRankingHelp from './pages/SearchRankingHelp';
import NotFoundPage from './pages/NotFoundPage';
import { NotificationProvider } from './components/NotificationProvider';
import CookieConsent from './components/CookieConsent';
import InstallAppBanner from './components/InstallAppBanner';
import LegalAcceptanceModal from './components/LegalAcceptanceModal';
import AccountTypeSelectionModal from './components/AccountTypeSelectionModal';
import {
  AUTH_ERROR_EVENT,
  AUTH_SUCCESS_EVENT,
  clearLegacyAuthStorage,
  hasAuthSessionHint,
  validateAndRefreshTokens,
  logout,
  isAdmin as checkIsAdmin,
  getUserAccountType,
  setUserAccountType as storeUserAccountType,
} from './services/auth';
import type { UserAccountType } from './services/api.types';
import { UnreadMessagesProvider } from './contexts/UnreadMessagesContext';
import { createPageUrl, getAppRoute, type AppPage } from './utils/appRoutes';
import { isAdminSite, publicSiteUrl, siteRedirectTarget } from './utils/siteHosts';
import { applyPageMetadata, STATIC_PAGE_METADATA } from './utils/pageMetadata';
import { completeNativeStartup } from './services/nativeStartup';
import { reloadOrCheckForAppUpdate } from './services/appUpdate';
import { setAnalyticsSession, trackPageView } from './services/analytics';
import './App.css';

// Only admins open this section, so it is a separate download instead of part of every visit.
const Administration = lazy(() => import('./pages/Administration'));

interface SearchParams {
  query?: string;
  location?: string;
  locationId?: string;
  categoryId?: string;
  subCategoryId?: string;
  productType?: 'PRODUCT' | 'DEMAND';
}

const INDEXABLE_PAGES = new Set<AppPage>([
  'home',
  'productDetail',
  'userDetails',
  'terms',
  'privacy',
  'cookies',
  'about',
  'contact',
  'searchRankingHelp',
]);

// These pages set their own title and description once their content has loaded.
const PAGES_WITH_OWN_METADATA = new Set<AppPage>(['productDetail', 'userDetails', 'notFound']);

const createEmptyRegisterDraft = (): RegisterDraft => ({
  formData: {
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  },
  agreedToTerms: false,
  marketingEmails: false
});

const getSearchParams = (params: URLSearchParams): SearchParams => {
  return {
    query: params.get('query') || undefined,
    location: params.get('location') || undefined,
    locationId: params.get('locationId') || undefined,
    categoryId: params.get('categoryId') || undefined,
    subCategoryId: params.get('subCategoryId') || undefined,
    productType: (params.get('productType') as 'PRODUCT' | 'DEMAND') || undefined,
  };
};

function App() {
  const initialRoute = getAppRoute();
  const [hadAuthSessionHintOnStartup] = useState<boolean>(() => hasAuthSessionHint());
  const [currentPage, setCurrentPage] = useState<AppPage>(initialRoute.page);
  const [searchParams, setSearchParams] = useState<SearchParams>(
    initialRoute.page === 'search' ? getSearchParams(initialRoute.params) : {}
  );
  const [productId, setProductId] = useState<string>(initialRoute.productId || '');
  const [messagesProductId, setMessagesProductId] = useState<string | null>(
    initialRoute.page === 'messages' ? initialRoute.params.get('productId') : null
  );
  const [messagesSearchTerm, setMessagesSearchTerm] = useState(
    initialRoute.page === 'messages' ? initialRoute.params.get('searchTerm') || '' : ''
  );
  const [userId, setUserId] = useState<string>(initialRoute.userId || '');
  const [resetToken, setResetToken] = useState<string>(
    initialRoute.page === 'resetPassword' ? initialRoute.token || '' : ''
  );
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(hadAuthSessionHintOnStartup);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [emailVerificationToken, setEmailVerificationToken] = useState<string>(
    initialRoute.page === 'verifyEmail' ? initialRoute.token || '' : ''
  );
  const [isAuthChecking, setIsAuthChecking] = useState<boolean>(true);
  const [userAccountType, setUserAccountType] = useState<UserAccountType | null>(null);
  const [isAccountTypeSelectionOpen, setIsAccountTypeSelectionOpen] = useState(false);
  const [registerDraft, setRegisterDraft] = useState<RegisterDraft>(() => createEmptyRegisterDraft());

  useEffect(() => {
    reloadOrCheckForAppUpdate();
  }, [currentPage]);

  useEffect(() => {
    let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.appendChild(robots);
    }
    robots.content = INDEXABLE_PAGES.has(currentPage) ? 'index, follow' : 'noindex, nofollow';

    const metadata = STATIC_PAGE_METADATA[currentPage];
    if (metadata) {
      applyPageMetadata(metadata);
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = `${window.location.origin}${metadata.path}`;
    } else if (currentPage === 'notFound') {
      document.querySelector('link[rel="canonical"]')?.remove();
    } else if (!PAGES_WITH_OWN_METADATA.has(currentPage) && STATIC_PAGE_METADATA.home) {
      applyPageMetadata(STATIC_PAGE_METADATA.home);
    }
  }, [currentPage]);

  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    const pendingAnimationFrames: number[] = [];
    window.history.scrollRestoration = 'manual';

    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    const resetScrollAfterHistoryNavigation = () => {
      scrollToTop();
      pendingAnimationFrames.push(window.requestAnimationFrame(() => {
        scrollToTop();
        pendingAnimationFrames.push(window.requestAnimationFrame(scrollToTop));
      }));
    };

    const handleUrlChange = (event?: PopStateEvent) => {
      const route = getAppRoute();

      if (route.productId) {
        setProductId(route.productId);
      }
      if (route.userId) {
        setUserId(route.userId);
      }
      if (route.page === 'search') {
        setSearchParams(getSearchParams(route.params));
      }
      if (route.page === 'messages') {
        setMessagesProductId(route.params.get('productId'));
        setMessagesSearchTerm(route.params.get('searchTerm') || '');
      }
      if (route.page === 'resetPassword') {
        setResetToken(route.token || '');
      }
      if (route.page === 'verifyEmail') {
        setEmailVerificationToken(route.token || '');
      }

      setCurrentPage(route.page);
      if (event) {
        resetScrollAfterHistoryNavigation();
      }
    };

    handleUrlChange();

    window.addEventListener('popstate', handleUrlChange);
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      pendingAnimationFrames.forEach(window.cancelAnimationFrame);
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  // The admin section has its own site; a page reached on the wrong one opens on the right one.
  useEffect(() => {
    const target = siteRedirectTarget(currentPage);
    if (target) {
      window.location.replace(target);
    }
  }, [currentPage]);

  useEffect(() => {
    const protectedPages = ['profile', 'favorites', 'settings', 'locations', 'myAnnouncements', 'createProduct', 'editProduct', 'messages', 'orders', 'orderStatistics', 'orderReport'];
    const adminPages = ['administration'];

    const adminAllowedPages = ['administration', 'productDetail', 'userDetails', 'notFound'];
    if (!isAuthChecking && isLoggedIn && isAdmin && !adminAllowedPages.includes(currentPage)) {
      window.scrollTo(0, 0);
      setCurrentPage('administration');
      window.history.replaceState({}, '', createPageUrl('administration'));
      return;
    }

    if (!isAuthChecking && !isLoggedIn && protectedPages.includes(currentPage)) {
      navigateToHome();
    }

    if (!isAuthChecking && (!isLoggedIn || !isAdmin) && adminPages.includes(currentPage)) {
      if (!isAdminSite()) {
        navigateToHome();
      } else if (!isLoggedIn) {
        navigateToLogin();
      } else {
        // A signed-in user without admin rights has nothing to do on the admin site.
        window.location.replace(`${publicSiteUrl()}/`);
      }
    }
  }, [currentPage, isLoggedIn, isAdmin, isAuthChecking]);

  useEffect(() => {
    if (userAccountType) {
      setIsAccountTypeSelectionOpen(false);
      return;
    }
    if (!isAuthChecking && isLoggedIn && !isAdmin && currentPage === 'createProduct') {
      setIsAccountTypeSelectionOpen(true);
    }
  }, [currentPage, isAdmin, isAuthChecking, isLoggedIn, userAccountType]);

  useEffect(() => {
    const handleAuthError = () => {
      setIsLoggedIn(false);
      setIsAdmin(false);
      setUserAccountType(null);
      setIsAccountTypeSelectionOpen(false);
    };
    const handleAuthSuccess = () => {
      setIsLoggedIn(true);
      setIsAdmin(checkIsAdmin());
      setUserAccountType(getUserAccountType());
    };

    window.addEventListener(AUTH_ERROR_EVENT, handleAuthError);
    window.addEventListener(AUTH_SUCCESS_EVENT, handleAuthSuccess);
    return () => {
      window.removeEventListener(AUTH_ERROR_EVENT, handleAuthError);
      window.removeEventListener(AUTH_SUCCESS_EVENT, handleAuthSuccess);
    };
  }, []);

  useEffect(() => {
    const validateTokensOnMount = async () => {
      clearLegacyAuthStorage();
      try {
        const isValid = await validateAndRefreshTokens(true);
        if (isValid) {
          setIsLoggedIn(true);
          setIsAdmin(checkIsAdmin());
          setUserAccountType(getUserAccountType());
        } else {
          setIsLoggedIn(false);
          setIsAdmin(false);
          setUserAccountType(null);
        }
      } catch (error) {
        setIsLoggedIn(false);
        setIsAdmin(false);
        setUserAccountType(null);
      }
      setIsAuthChecking(false);
    };

    validateTokensOnMount();
  }, []);

  useEffect(() => {
    if (!isAuthChecking) {
      completeNativeStartup();
    }
  }, [isAuthChecking]);

  useEffect(() => {
    setAnalyticsSession({ authResolved: !isAuthChecking, isAdmin: isLoggedIn && isAdmin });
  }, [isAuthChecking, isLoggedIn, isAdmin]);

  useEffect(() => {
    const entityId = currentPage === 'productDetail' || currentPage === 'editProduct'
      ? productId
      : currentPage === 'userDetails' ? userId : '';
    trackPageView(currentPage, `${currentPage}:${entityId}`);
  }, [currentPage, productId, userId]);

  const updateUrl = (page: AppPage, params?: SearchParams) => {
    window.history.pushState(
      {},
      '',
      createPageUrl(page, params ? { ...params } : {})
    );
  };

  const navigateToHome = () => {
    window.scrollTo(0, 0);
    setCurrentPage('home');
    updateUrl('home');
  };

  const navigateToLogin = () => {
    window.scrollTo(0, 0);
    setCurrentPage('login');
    updateUrl('login');
  };

  const handleLogin = () => {
    const admin = checkIsAdmin();
    setIsLoggedIn(true);
    setIsAdmin(admin);
    setUserAccountType(getUserAccountType());
    if (admin) {
      window.scrollTo(0, 0);
      setCurrentPage('administration');
      updateUrl('administration');
    } else {
      navigateToHome();
    }
  };

  const handleRegister = () => {
    setRegisterDraft(createEmptyRegisterDraft());
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggedIn(false);
      setIsAdmin(false);
      setUserAccountType(null);
      setIsAccountTypeSelectionOpen(false);
      if (isAdminSite()) {
        navigateToLogin();
      } else {
        navigateToHome();
      }
    }
  };

  const navigateToRegister = () => {
    window.scrollTo(0, 0);
    setCurrentPage('register');
    updateUrl('register');
  };

  const navigateToSearch = (params: SearchParams) => {
    window.scrollTo(0, 0);
    setSearchParams(params);
    setCurrentPage('search');
    updateUrl('search', params);
  };

  const navigateToProductDetail = (id: string) => {
    window.scrollTo(0, 0);
    setProductId(id);
    setCurrentPage('productDetail');
    window.history.pushState({}, '', createPageUrl('productDetail', { id }));
  };

  const navigateToUserDetails = (id: string, publicName?: string) => {
    window.scrollTo(0, 0);
    setUserId(id);
    setCurrentPage('userDetails');
    window.history.pushState({}, '', createPageUrl('userDetails', { id, name: publicName }));
  };

  const navigateToProfile = () => {
    window.scrollTo(0, 0);
    setCurrentPage('profile');
    updateUrl('profile');
  };

  const navigateToSettings = () => {
    window.scrollTo(0, 0);
    setCurrentPage('settings');
    updateUrl('settings');
  };

  const navigateToLocations = () => {
    window.scrollTo(0, 0);
    setCurrentPage('locations');
    updateUrl('locations');
  };

  const navigateToMyAnnouncements = (productType?: 'PRODUCT' | 'DEMAND') => {
    window.scrollTo(0, 0);
    setCurrentPage('myAnnouncements');
    updateUrl('myAnnouncements', productType ? { productType } : undefined);
  };

  const openCreateProductPage = () => {
    window.scrollTo(0, 0);
    setCurrentPage('createProduct');
    updateUrl('createProduct');
  };

  const navigateToCreateProduct = () => {
    if (!isLoggedIn) {
      navigateToLogin();
      return;
    }
    if (!userAccountType) {
      setIsAccountTypeSelectionOpen(true);
      return;
    }
    openCreateProductPage();
  };

  const navigateToEditProduct = (id: string) => {
    if (!isLoggedIn) {
      navigateToLogin();
      return;
    }
    window.scrollTo(0, 0);
    setProductId(id);
    setCurrentPage('editProduct');
    window.history.pushState({}, '', createPageUrl('editProduct', { id }));
  };

  const navigateToMessages = (productId?: string | null, searchTerm?: string) => {
    if (!isLoggedIn) {
      navigateToLogin();
      return;
    }
    window.scrollTo(0, 0);
    setMessagesProductId(productId || null);
    setMessagesSearchTerm(searchTerm || '');
    setCurrentPage('messages');
    const urlParams: Record<string, string> = {};
    if (productId) {
      urlParams.productId = productId;
    }
    if (searchTerm) {
      urlParams.searchTerm = searchTerm;
    }
    window.history.pushState({}, '', createPageUrl('messages', urlParams));
  };

  const navigateToAdministration = () => {
    if (!isLoggedIn || !isAdmin) {
      navigateToHome();
      return;
    }
    window.scrollTo(0, 0);
    setCurrentPage('administration');
    updateUrl('administration');
  };

  const navigateToTerms = () => {
    window.scrollTo(0, 0);
    setCurrentPage('terms');
    updateUrl('terms');
  };

  const navigateToPrivacy = () => {
    window.scrollTo(0, 0);
    setCurrentPage('privacy');
    updateUrl('privacy');
  };

  const navigateToCookies = () => {
    window.scrollTo(0, 0);
    setCurrentPage('cookies');
    updateUrl('cookies');
  };

  const navigateToAbout = () => {
    window.scrollTo(0, 0);
    setCurrentPage('about');
    updateUrl('about');
  };

  const navigateToContact = () => {
    window.scrollTo(0, 0);
    setCurrentPage('contact');
    updateUrl('contact');
  };

  const navigateToSearchRankingHelp = () => {
    window.scrollTo(0, 0);
    setCurrentPage('searchRankingHelp');
    updateUrl('searchRankingHelp');
  };

  return (
    <NotificationProvider>
      <UnreadMessagesProvider isLoggedIn={isLoggedIn && !isAdmin && !isAuthChecking}>
        <div className="App">
          {isAuthChecking && !hadAuthSessionHintOnStartup && (
            <div className="auth-startup-cover" role="status" aria-label="Se verifică sesiunea">
              <img src="/icons/icon-192.png?v=15" alt="" />
              <span className="auth-startup-spinner" aria-hidden="true" />
            </div>
          )}
          <InstallAppBanner />
      {currentPage === 'home' && (
        <Home
          isLoggedIn={isLoggedIn}
          isAdmin={isAdmin}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToSearch={navigateToSearch}
          onNavigateToProductDetail={navigateToProductDetail}
          onLogout={handleLogout}
          onNavigateToProfile={navigateToProfile}
          onNavigateToLocations={navigateToLocations}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onNavigateToSettings={navigateToSettings}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToMessages={navigateToMessages}
          onNavigateToAdministration={navigateToAdministration}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
          onNavigateToAbout={navigateToAbout}
          onNavigateToContact={navigateToContact}
        />
      )}
      {currentPage === 'login' && (
        <Login
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onLogin={handleLogin}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
        />
      )}
      {currentPage === 'register' && (
        <Register
          onNavigateToLogin={navigateToLogin}
          onNavigateToHome={navigateToHome}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onRegistrationComplete={handleRegister}
          onGoogleSignIn={handleLogin}
          draft={registerDraft}
          onDraftChange={setRegisterDraft}
        />
      )}
      {currentPage === 'resetPassword' && (
        <ResetPassword
          token={resetToken}
          onNavigateToLogin={navigateToLogin}
          onNavigateToHome={navigateToHome}
        />
      )}
      {currentPage === 'verifyEmail' && (
        <VerifyEmail
          token={emailVerificationToken}
          onNavigateToLogin={navigateToLogin}
          onNavigateToHome={navigateToHome}
        />
      )}
      {currentPage === 'search' && (
        <SearchResults
          isLoggedIn={isLoggedIn}
          searchParams={searchParams}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onNavigateToProductDetail={navigateToProductDetail}
          onLogout={handleLogout}
          onNavigateToProfile={navigateToProfile}
          onNavigateToLocations={navigateToLocations}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onNavigateToSettings={navigateToSettings}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToMessages={navigateToMessages}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
          onNavigateToAbout={navigateToAbout}
          onNavigateToContact={navigateToContact}
          onNavigateToSearchRankingHelp={navigateToSearchRankingHelp}
        />
      )}
      {currentPage === 'productDetail' && (
        <ProductDetail
          isLoggedIn={isLoggedIn}
          isAdmin={isAdmin}
          productId={productId}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onNavigateToProductDetail={navigateToProductDetail}
          onNavigateToUserDetails={navigateToUserDetails}
          onLogout={handleLogout}
          onNavigateToProfile={navigateToProfile}
          onNavigateToLocations={navigateToLocations}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onNavigateToSettings={navigateToSettings}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToMessages={navigateToMessages}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
          onNavigateToAbout={navigateToAbout}
          onNavigateToContact={navigateToContact}
          onNavigateToAdministration={navigateToAdministration}
        />
      )}
      {currentPage === 'userDetails' && (
        <UserDetails
          isLoggedIn={isLoggedIn}
          isAdmin={isAdmin}
          userId={userId}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onNavigateToProductDetail={navigateToProductDetail}
          onLogout={handleLogout}
          onNavigateToProfile={navigateToProfile}
          onNavigateToLocations={navigateToLocations}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onNavigateToSettings={navigateToSettings}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToMessages={navigateToMessages}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
          onNavigateToAbout={navigateToAbout}
          onNavigateToContact={navigateToContact}
          onNavigateToAdministration={navigateToAdministration}
        />
      )}
      {currentPage === 'profile' && (
        <Profile
          isLoggedIn={isLoggedIn}
          isAuthChecking={isAuthChecking}
          isAdmin={isAdmin}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onNavigateToLocations={navigateToLocations}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onNavigateToSettings={navigateToSettings}
          onNavigateToAdministration={navigateToAdministration}
          onLogout={handleLogout}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToMessages={navigateToMessages}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
          onNavigateToAbout={navigateToAbout}
          onNavigateToContact={navigateToContact}
        />
      )}
      {currentPage === 'settings' && (
        <Settings
          isLoggedIn={isLoggedIn}
          isAuthChecking={isAuthChecking}
          isAdmin={isAdmin}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onNavigateToProfile={navigateToProfile}
          onNavigateToLocations={navigateToLocations}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onNavigateToAdministration={navigateToAdministration}
          onLogout={handleLogout}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToMessages={navigateToMessages}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
          onNavigateToAbout={navigateToAbout}
          onAccountTypeChange={setUserAccountType}
        />
      )}
      {currentPage === 'locations' && (
        <Locations
          isLoggedIn={isLoggedIn}
          isAuthChecking={isAuthChecking}
          isAdmin={isAdmin}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onNavigateToProfile={navigateToProfile}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onNavigateToSettings={navigateToSettings}
          onNavigateToAdministration={navigateToAdministration}
          onLogout={handleLogout}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToMessages={navigateToMessages}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
        />
      )}
      {currentPage === 'favorites' && (
        <Favorites
          isLoggedIn={isLoggedIn}
          isAuthChecking={isAuthChecking}
          isAdmin={isAdmin}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onNavigateToProfile={navigateToProfile}
          onNavigateToLocations={navigateToLocations}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onNavigateToSettings={navigateToSettings}
          onNavigateToAdministration={navigateToAdministration}
          onLogout={handleLogout}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToMessages={navigateToMessages}
          onNavigateToProductDetail={navigateToProductDetail}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
          onNavigateToAbout={navigateToAbout}
          onNavigateToContact={navigateToContact}
        />
      )}
      {currentPage === 'myAnnouncements' && (
        <MyAnnouncements
          isLoggedIn={isLoggedIn}
          isAuthChecking={isAuthChecking}
          isAdmin={isAdmin}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onNavigateToProfile={navigateToProfile}
          onNavigateToLocations={navigateToLocations}
          onNavigateToSettings={navigateToSettings}
          onNavigateToAdministration={navigateToAdministration}
          onLogout={handleLogout}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToProductDetail={navigateToProductDetail}
          onNavigateToEditProduct={navigateToEditProduct}
          onNavigateToMessages={navigateToMessages}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
        />
      )}
      {currentPage === 'createProduct' && (
        <CreateProduct
          isLoggedIn={isLoggedIn}
          isAuthChecking={isAuthChecking}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onNavigateToProfile={navigateToProfile}
          onNavigateToLocations={navigateToLocations}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onLogout={handleLogout}
          onNavigateToMessages={navigateToMessages}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
          onNavigateToAbout={navigateToAbout}
          onNavigateToContact={navigateToContact}
        />
      )}
      {currentPage === 'editProduct' && (
        <EditProduct
          isLoggedIn={isLoggedIn}
          isAuthChecking={isAuthChecking}
          productId={productId}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onNavigateToProfile={navigateToProfile}
          onNavigateToLocations={navigateToLocations}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onLogout={handleLogout}
          onNavigateToMessages={navigateToMessages}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
          onNavigateToAbout={navigateToAbout}
          onNavigateToContact={navigateToContact}
        />
      )}
      {currentPage === 'messages' && (
        <Messages
          isLoggedIn={isLoggedIn}
          isAuthChecking={isAuthChecking}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onNavigateToProfile={navigateToProfile}
          onNavigateToLocations={navigateToLocations}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onNavigateToSettings={navigateToSettings}
          onLogout={handleLogout}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToProductDetail={navigateToProductDetail}
          onNavigateToUserDetails={navigateToUserDetails}
          productId={messagesProductId}
          initialSearchTerm={messagesSearchTerm}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
        />
      )}
      {currentPage === 'orders' && (
        <Orders
          isLoggedIn={isLoggedIn}
          isAuthChecking={isAuthChecking}
          isAdmin={isAdmin}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onNavigateToProfile={navigateToProfile}
          onNavigateToLocations={navigateToLocations}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onNavigateToSettings={navigateToSettings}
          onNavigateToAdministration={navigateToAdministration}
          onLogout={handleLogout}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToMessages={navigateToMessages}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
          onNavigateToAbout={navigateToAbout}
          onNavigateToContact={navigateToContact}
        />
      )}
      {currentPage === 'orderReport' && (
        <OrderReport
          isLoggedIn={isLoggedIn}
          isAuthChecking={isAuthChecking}
          isAdmin={isAdmin}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onNavigateToProfile={navigateToProfile}
          onNavigateToLocations={navigateToLocations}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onNavigateToSettings={navigateToSettings}
          onNavigateToAdministration={navigateToAdministration}
          onLogout={handleLogout}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToMessages={navigateToMessages}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
          onNavigateToAbout={navigateToAbout}
          onNavigateToContact={navigateToContact}
        />
      )}
      {currentPage === 'orderStatistics' && (
        <OrderStatistics
          isLoggedIn={isLoggedIn}
          isAuthChecking={isAuthChecking}
          isAdmin={isAdmin}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onNavigateToProfile={navigateToProfile}
          onNavigateToLocations={navigateToLocations}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onNavigateToSettings={navigateToSettings}
          onNavigateToAdministration={navigateToAdministration}
          onLogout={handleLogout}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToMessages={navigateToMessages}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
          onNavigateToAbout={navigateToAbout}
          onNavigateToContact={navigateToContact}
        />
      )}
      {currentPage === 'administration' && (
        <Suspense fallback={<LoadingIndicator message={t.common.loading} variant="page" />}>
        <Administration
          isLoggedIn={isLoggedIn}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onNavigateToProfile={navigateToProfile}
          onNavigateToLocations={navigateToLocations}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onNavigateToSettings={navigateToSettings}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToMessages={navigateToMessages}
          onNavigateToProductDetail={navigateToProductDetail}
          onNavigateToUserDetails={navigateToUserDetails}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
          onNavigateToAbout={navigateToAbout}
          onNavigateToContact={navigateToContact}
          onLogout={handleLogout}
        />
        </Suspense>
      )}
      {(currentPage === 'terms' || currentPage === 'privacy' || currentPage === 'cookies') && (
        <LegalPage
          key={currentPage}
          documentType={currentPage === 'terms' ? 'TERMS' : currentPage === 'privacy' ? 'PRIVACY' : 'COOKIES'}
          isLoggedIn={isLoggedIn}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onLogout={handleLogout}
          onNavigateToProfile={navigateToProfile}
          onNavigateToLocations={navigateToLocations}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToMessages={navigateToMessages}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
          onNavigateToAbout={navigateToAbout}
          onNavigateToContact={navigateToContact}
        />
      )}
      {currentPage === 'about' && (
        <AboutPage
          isLoggedIn={isLoggedIn}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onLogout={handleLogout}
          onNavigateToProfile={navigateToProfile}
          onNavigateToLocations={navigateToLocations}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToMessages={navigateToMessages}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
          onNavigateToContact={navigateToContact}
        />
      )}
      {currentPage === 'contact' && (
        <ContactPage
          isLoggedIn={isLoggedIn}
          isAdmin={isAdmin}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onLogout={handleLogout}
          onNavigateToProfile={navigateToProfile}
          onNavigateToLocations={navigateToLocations}
          onNavigateToSettings={navigateToSettings}
          onNavigateToAdministration={navigateToAdministration}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToMessages={navigateToMessages}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
          onNavigateToAbout={navigateToAbout}
        />
      )}
      {currentPage === 'searchRankingHelp' && (
        <SearchRankingHelp
          isLoggedIn={isLoggedIn}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onLogout={handleLogout}
          onNavigateToProfile={navigateToProfile}
          onNavigateToLocations={navigateToLocations}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToMessages={navigateToMessages}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
          onNavigateToAbout={navigateToAbout}
          onNavigateToContact={navigateToContact}
        />
      )}
      {currentPage === 'notFound' && (
        <NotFoundPage
          isLoggedIn={isLoggedIn}
          isAdmin={isAdmin}
          onNavigateToLogin={navigateToLogin}
          onNavigateToRegister={navigateToRegister}
          onNavigateToHome={navigateToHome}
          onNavigateToSearch={() => navigateToSearch({})}
          onLogout={handleLogout}
          onNavigateToProfile={navigateToProfile}
          onNavigateToLocations={navigateToLocations}
          onNavigateToMyAnnouncements={navigateToMyAnnouncements}
          onNavigateToSettings={navigateToSettings}
          onNavigateToCreateProduct={navigateToCreateProduct}
          onNavigateToMessages={navigateToMessages}
          onNavigateToAdministration={navigateToAdministration}
          onNavigateToTerms={navigateToTerms}
          onNavigateToPrivacy={navigateToPrivacy}
          onNavigateToCookies={navigateToCookies}
          onNavigateToAbout={navigateToAbout}
          onNavigateToContact={navigateToContact}
        />
      )}
      <CookieConsent onNavigateToCookies={navigateToCookies} />
      {!isAuthChecking && isLoggedIn && !isAdmin && !userAccountType && isAccountTypeSelectionOpen && (
        <AccountTypeSelectionModal
          onComplete={(accountType) => {
            storeUserAccountType(accountType);
            setUserAccountType(accountType);
            setIsAccountTypeSelectionOpen(false);
            if (currentPage !== 'createProduct') {
              openCreateProductPage();
            }
          }}
          onCancel={() => {
            setIsAccountTypeSelectionOpen(false);
            if (currentPage === 'createProduct') {
              navigateToHome();
            }
          }}
        />
      )}
      <LegalAcceptanceModal
        enabled={!isAuthChecking && isLoggedIn && !isAdmin}
        onDecline={() => void handleLogout()}
      />
        </div>
      </UnreadMessagesProvider>
    </NotificationProvider>
  );
}

export default App;
